import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { fetchMetadata, submitReport } from "../lib/api";
import { BRANDING } from "../lib/branding";
import { DisclaimerBanner } from "./DisclaimerBanner";
import { Loader2, RefreshCw } from "lucide-react";
import { isUrlInNote, NOTE_MAX_LENGTH } from "../lib/reportValidation";

const REPORT_OPTIONS = [
  { v: "accepting", label: "Accepting new patients" },
  { v: "not_accepting", label: "Not accepting new patients" },
  { v: "unknown", label: "Unknown / could not confirm" },
];

export const ReportDialog = ({ open, onOpenChange, doctor, onSubmitted }) => {
  const [reportType, setReportType] = useState("");
  const [note, setNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const refreshCaptcha = useCallback(async () => {
    try {
      const data = await fetchMetadata();

      if (data?.captcha?.id && data?.captcha?.question) {
        setCaptcha(data.captcha);
        setCaptchaAnswer("");
        setFormMessage("");
      } else {
        setCaptcha(null);
        setFormMessage("CAPTCHA could not load. Please refresh it.");
      }
    } catch {
      setCaptcha(null);
      setFormMessage("CAPTCHA could not load. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    setReportType("");
    setNote("");
    setConsent(false);
    setCaptcha(null);
    setCaptchaAnswer("");
    setFormMessage("");
    setSuccessMessage("");
    refreshCaptcha();
  }, [open, refreshCaptcha]);

  const handleSubmit = async () => {
    setFormMessage("");
    setSuccessMessage("");

    if (!doctor?.id) {
      setFormMessage("Doctor information is missing. Please refresh and try again.");
      return;
    }

    if (!reportType) {
      setFormMessage("Please select what you found.");
      return;
    }

    if (note.length > NOTE_MAX_LENGTH) {
      setFormMessage(`Note must be ${NOTE_MAX_LENGTH} characters or fewer.`);
      return;
    }

    if (isUrlInNote(note)) {
      setFormMessage("URLs are not allowed in report notes.");
      return;
    }

    if (!captcha?.id) {
      setFormMessage("CAPTCHA did not load. Please refresh it.");
      return;
    }

    if (!captchaAnswer.trim()) {
      setFormMessage("Please answer the CAPTCHA.");
      return;
    }

    if (!consent) {
      setFormMessage("Please confirm you will not include personal health information.");
      return;
    }

    setLoading(true);

    try {
      const response = await submitReport({
        doctor_id: doctor.id,
        report_type: reportType,
        note: note.trim(),
        consent,
        captcha_question_id: captcha.id,
        captcha_answer: captchaAnswer,
      });

      if (response?.success || response?.ok) {
        setSuccessMessage(
          response?.message || "Thank you. Your report was submitted for admin review."
        );

        setReportType("");
        setNote("");
        setConsent(false);
        setCaptchaAnswer("");

        setTimeout(() => {
          onSubmitted?.(response);
          onOpenChange(false);
        }, 1800);

        return;
      }

      setFormMessage(response?.message || "Unable to submit report.");
    } catch (error) {
      setFormMessage(error?.message || "Something went wrong while submitting your report.");
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const noteUrlInvalid = isUrlInNote(note);
  const noteTooLong = note.length > NOTE_MAX_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog" className="sm:max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Report availability update
          </DialogTitle>

          <DialogDescription>
            Share what you experienced when contacting{" "}
            <span className="font-semibold text-slate-700">
              {doctor?.name || "this provider"}
            </span>
            . This helps other Ontario residents.
          </DialogDescription>
        </DialogHeader>

        <DisclaimerBanner variant="inline" />

        {formMessage && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {formMessage}
          </div>
        )}

        {successMessage && (
          <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
            {successMessage}
          </div>
        )}

        <div className="space-y-5 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              What did you find? <span className="text-red-500">*</span>
            </Label>

            <RadioGroup
              value={reportType}
              onValueChange={setReportType}
              className="mt-3 grid gap-2"
              data-testid="report-type-group"
            >
              {REPORT_OPTIONS.map((opt) => (
                <label
                  key={opt.v}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:bg-white"
                >
                  <RadioGroupItem
                    value={opt.v}
                    id={`rt-${opt.v}`}
                    data-testid={`report-type-${opt.v}`}
                  />
                  <span className="text-sm text-slate-700">{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label
              htmlFor="note"
              className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
            >
              Optional note (max {NOTE_MAX_LENGTH} chars)
            </Label>

            <Textarea
              id="note"
              data-testid="report-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={NOTE_MAX_LENGTH}
              rows={3}
              placeholder="E.g., front desk said waitlist is open — no PHI, no URLs."
              className="mt-2 rounded-2xl bg-slate-50 border-slate-200 focus-visible:ring-[#2C5E7A]/30"
            />

            <div
              className={`mt-1 text-xs ${
                noteTooLong || noteUrlInvalid ? "text-red-600" : "text-slate-500"
              }`}
            >
              {note.length}/{NOTE_MAX_LENGTH} — Do not include personal health info or URLs.
            </div>

            {noteUrlInvalid && (
              <p className="mt-1 text-xs text-red-600">
                URLs are not allowed in notes.
              </p>
            )}
          </div>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              CAPTCHA <span className="text-red-500">*</span>
            </Label>

            <div className="mt-2 flex items-center gap-3">
              <span
                data-testid="captcha-question"
                className="font-heading font-semibold text-slate-800 min-w-[90px]"
              >
                {captcha?.question || "Loading..."}
              </span>

              <Input
                data-testid="captcha-answer"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                inputMode="numeric"
                className="w-24 rounded-xl bg-white"
                placeholder="="
              />

              <button
                type="button"
                onClick={refreshCaptcha}
                className="text-slate-500 hover:text-[#1E4256]"
                data-testid="captcha-refresh"
                aria-label="refresh captcha"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              data-testid="consent-checkbox"
              className="mt-0.5"
            />

            <span className="text-sm text-slate-700">
              {BRANDING.consentText ||
                "I will not include personal health information."}
            </span>
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="report-cancel"
          >
            Cancel
          </Button>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="rounded-full bg-[#2C5E7A] hover:bg-[#1E4256]"
            data-testid="report-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
